import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { offlineQueue, isNetworkError } from '@/lib/offlineQueue';

export default function usePhotoQueue(registrationId, onUploaded) {
  const [queue, setQueue] = useState([]);
  const [flushing, setFlushing] = useState(false);
  const [online, setOnline] = useState(typeof navigator === 'undefined' ? true : navigator.onLine);
  const busy = useRef(false);

  const refresh = useCallback(async () => {
    try { setQueue(await offlineQueue.list(registrationId)); } catch { setQueue([]); }
  }, [registrationId]);

  const flush = useCallback(async (silent = false) => {
    if (busy.current || !navigator.onLine) return;
    const items = await offlineQueue.list(registrationId);
    if (!items.length) return;
    busy.current = true;
    setFlushing(true);
    let ok = 0, failed = 0;
    for (const item of items) {
      const fd = new FormData();
      fd.append('photo', new File([item.blob], item.fileName, { type: item.mimeType }));
      fd.append('waypoint_id', item.waypointId);
      try {
        const r = await axios.post(`${API}/registrations/${registrationId}/tracking-photos`, fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000 });
        await offlineQueue.remove(item.id);
        ok += 1;
        const p = r.data?.photo;
        if (!silent || ok === 1) toast.success(`${item.waypointName}: ${p?.status === 'verified' ? 'acreditado ✓' : 'subida, en revisión'}`);
      } catch (e) {
        if (isNetworkError(e) || e.response?.status >= 500) {
          failed += 1;
          await offlineQueue.update({ ...item, attempts: (item.attempts || 0) + 1, lastError: e.response?.data?.detail || e.message });
          break;
        }
        await offlineQueue.remove(item.id);
        toast.error(`${item.waypointName}: ${e.response?.data?.detail || 'rechazada por el servidor'}`);
      }
    }
    busy.current = false;
    setFlushing(false);
    await refresh();
    if (ok) onUploaded?.();
    if (failed && !silent) toast.info('Sin conexión: las fotos siguen guardadas en el móvil');
  }, [registrationId, refresh, onUploaded]);

  const enqueue = useCallback(async (wp, file) => {
    await offlineQueue.enqueuePhoto({ registrationId, waypointId: wp.id, waypointName: wp.name, file });
    await refresh();
  }, [registrationId, refresh]);

  const remove = useCallback(async (id) => { await offlineQueue.remove(id); await refresh(); }, [refresh]);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    const goOnline = () => { setOnline(true); flush(true); };
    const goOffline = () => setOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    const t = setTimeout(() => flush(true), 1500);
    const iv = setInterval(() => flush(true), 60000);
    return () => { window.removeEventListener('online', goOnline); window.removeEventListener('offline', goOffline); clearTimeout(t); clearInterval(iv); };
  }, [flush]);

  return { queue, flushing, online, enqueue, flush, remove };
}
